import React, { useState, useEffect } from 'react';
//import { ApolloProvider as OriginalApolloProvider } from '@apollo/react-hooks'
import { ApolloProvider as OriginalApolloProvider } from 'react-apollo';
import { gql } from 'apollo-boost';
import { useHistory } from 'react-router-dom';

import { UserProvider, signOut } from '../auth';
import { useEffectiveUser } from '../auth/hooks';
import { createClient } from './client';
import { addHeader, removeHeader } from './request';
import TrueImpactLoading from '../components/TrueImpactLoading';
import TrueImpactErrorScreen from '../components/TrueImpactErrorScreen';
import { normalizeFeatures } from '../providers/FeatureFlags/useFeatureFlags';

export function ApolloAndUserProvider({ children }) {
  const [client, setClient] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [fatalError, setFatalError] = useState(null);
  const user = useEffectiveUser();
  const [idToken, setIdToken] = useState();
  const { push } = useHistory();

  useEffect(() => {
    // Check for CN Auth0 token first
    const cnToken = localStorage.getItem('cn_ti_token');
    if (cnToken) {
      setIdToken(cnToken);
      return;
    }

    // Fall back to Cognito token
    if (!user || !user.authUser) {
      return;
    }

    const {
      signInUserSession: {
        idToken: { jwtToken },
      },
    } = user.authUser;
    setIdToken(jwtToken);
  }, [user, setIdToken]);
  useEffect(() => {
    if (!idToken) {
      return;
    }

    // For CN Auth0, we don't need the user object
    const ghostedUsername = user && user.ghostedUsername;

    const headers = ghostedUsername
      ? {
          'ti-id-token': idToken,
          'ti-ghosted-user': ghostedUsername,
          //        'ti-ghosted-user-id': ghostedUser.attributes['custom:user_id'],
          //        'ti-ghosted-account-id': ghostedUser.attributes['custom:account_id'],
        }
      : {
          'ti-id-token': idToken,
        };
    const newClient = createClient(headers);
    setClient(newClient);

    for (const [key, value] of Object.entries(headers)) {
      addHeader(key, value);
    }
    if (!ghostedUsername) {
      removeHeader('ti-ghosted-user');
      //      removeHeader('ti-ghosted-user-id')
      //      removeHeader('ti-ghosted-account-id')
    }
  }, [idToken, user && user.ghostedUsername]);
  useEffect(() => {
    if (!client) {
      return;
    }
    const cnToken = localStorage.getItem('cn_ti_token');

    // For CN Auth0 users, we might not have a user object yet
    // Skip the account query if we don't have user info and we're using CN Auth0
    if (!user && !cnToken) {
      return;
    }

    let active = true;
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await getMyAccount(client);

        setIsLoading(false);
        if (!active) {
          return;
        }

        const {
          userId,
          id: accountId,
          type: accountType,
          firstName,
          lastName,
          userType,
          name: organizationName,
          logoFileUrl: organizationLogoUrl,
          logoFile: organizationLogo,
          userLogo,
          visibility,
          newProgramVisibility,
          features: rawFeatures,
        } = data.myAccount[0];
        const features = normalizeFeatures(rawFeatures);

        // Only assign to user if we have a user object (Cognito flow)
        if (user) {
          user.assign({
            accountType,
            email: user.ghostedUsername,
            userId,
            accountId,
            firstName,
            lastName,
            userType,
            organizationName,
            organizationLogoUrl,
            organizationLogo,
            userLogo,
            visibility,
            newProgramVisibility,
            features,
          });
        } else {
          const cnUserData = {
            userId,
            accountId,
            accountType,
            firstName,
            lastName,
            userType,
            organizationName,
            organizationLogoUrl,
            organizationLogo,
            userLogo,
            features,
          };
          localStorage.setItem('cn_user_data', JSON.stringify(cnUserData));
        }
      } catch (err) {
        setIsLoading(false);

        // Log error for debugging
        console.error('Apollo Provider myAccount query failed:', err);
        console.log('Error message:', err.message);
        console.log('Error details:', JSON.stringify(err, null, 2));

        // Handle partner auth failures differently
        const cnToken = localStorage.getItem('cn_ti_token');
        if (cnToken && err.message.includes('Authentication')) {
          const isBbPartner = localStorage.getItem('bb_partner') === 'true';
          localStorage.removeItem('cn_ti_token');
          localStorage.removeItem('cn_user_data');
          if (isBbPartner) {
            localStorage.removeItem('bb_partner');
            push('/bb-widget');
          } else {
            push('/cn-widget');
          }
          return;
        }

        if (err.message.includes('Login failed')) {
          console.warn('Login failed error detected, redirecting to /login');
          push('/login');
          await signOut();
          if (user) {
            user.signInAs(null);
          }
          alert('Incorrect username or password');
          window.location.reload();
          return;
        }

        console.error('Unhandled error in Apollo Provider:', err);
        if (active) {
          setFatalError(err);
        }
      }
    })();

    return () => {
      active = false;
      setIsLoading(false);
    };
  }, [client, push]);

  if (fatalError) {
    return (
      <TrueImpactErrorScreen
        error={fatalError}
        context="ApolloAndUserProvider: myAccount query"
        onRetry={() => {
          setFatalError(null);
          window.location.reload();
        }}
      />
    );
  }

  if (isLoading) {
    return <TrueImpactLoading />;
  }

  // Check if using CN Auth0 token
  const cnToken = localStorage.getItem('cn_ti_token');

  // For CN Auth0 users without Cognito user, create a fake user object
  let effectiveUser = user;
  if (cnToken && !user) {
    const cnUserDataStr = localStorage.getItem('cn_user_data');
    if (cnUserDataStr) {
      try {
        const cnUserData = JSON.parse(cnUserDataStr);
        const attributes = {
          'custom:user_id': cnUserData.userId,
          'custom:account_id': cnUserData.accountId,
          'custom:type': cnUserData.userType || 'basic',
          email: cnUserData.email,
          givenName: cnUserData.firstName,
          familyName: cnUserData.lastName,
          organizationName: cnUserData.organizationName,
          organizationLogoUrl: cnUserData.organizationLogoUrl,
          organizationLogo: cnUserData.organizationLogo,
          userLogo: cnUserData.userLogo,
          features: cnUserData.features || {},
        };
        // Create a user object with the same structure as Cognito user
        effectiveUser = {
          role: 'grantee',
          authUser: {
            username: cnUserData.email || 'cn_user',
            attributes,
          },
          attributes,
          effectiveUser: { attributes },
          ghostedUsername: null,
          password: null,
        };
      } catch (e) {
        console.error('Failed to parse cn_user_data:', e);
      }
    }
  }

  // If we don't have a client and we're not using CN Auth0, render UserProvider only
  if (!client && !cnToken) {
    return <UserProvider user={effectiveUser}>{children}</UserProvider>;
  }

  // If we have a client, render with Apollo
  if (client) {
    return (
      <OriginalApolloProvider client={client}>
        <UserProvider user={effectiveUser}>{children}</UserProvider>
      </OriginalApolloProvider>
    );
  }

  // Loading state for CN Auth0
  return <TrueImpactLoading />;
}

const accountQuery = gql`
  query {
    myAccount {
      id
      userId
      type
      visibility
      firstName
      lastName
      email
      userType
      name
      logoFileUrl
      logoFile
      userLogo
      newProgramVisibility
      features {
        name
        value
      }
    }
  }
`;

export function getMyAccount(client) {
  return client.query({ query: accountQuery, fetchPolicy: 'no-cache' });
}
